import type { AgendaEntry } from '@aeronexo/contracts';

export const scheduleStates: Readonly<Record<string, string>> = {
  draft: 'Rascunho',
  planned: 'Planejada',
  coordinating: 'Em coordenação',
  ready: 'Coordenação concluída',
  in_progress: 'Em execução',
  completed: 'Execução concluída',
  closed: 'Encerrada',
  cancelled: 'Cancelada',
};
export const cancellationReasons: Readonly<Record<string, string>> = {
  customer_request: 'Solicitação do cliente',
  weather: 'Meteorologia',
  technical: 'Motivo técnico',
  crew: 'Tripulação',
  airport: 'Aeródromo',
  commercial: 'Comercial',
  other: 'Outro motivo',
};
export const scheduleErrors: Readonly<Record<string, string>> = {
  SCHEDULE_REVISION_CONFLICT:
    'Outra pessoa alterou esta operação. Atualize o planejamento e revise os horários antes de tentar novamente.',
  SCHEDULE_IDEMPOTENCY_CONFLICT:
    'Esta tentativa já foi registrada com outros dados. Atualize o planejamento antes de continuar.',
  AIRCRAFT_SCHEDULE_CONFLICT:
    'A aeronave já está ocupada nesse intervalo. Consulte os conflitos e escolha outros horários.',
  RESPONSIBLE_SCHEDULE_CONFLICT:
    'O responsável tem outra operação nesse horário. Revise o conflito e informe uma justificativa.',
  SCHEDULE_STATE_CONFLICT:
    'O estado da operação mudou ou há trechos realizados/cancelados. Atualize o planejamento.',
  INVALID_SCHEDULE: 'Revise os horários, os trechos, o motivo e o impacto informados.',
  REAUTHENTICATION_FAILED:
    'Não foi possível confirmar sua identidade. Confira a senha; se a sessão expirou, entre novamente.',
  SCHEDULE_FORBIDDEN:
    'Seu acesso não permite esta alteração. Procure o administrador da organização.',
  SCHEDULE_OVERRIDE_FORBIDDEN: 'Seu acesso não permite confirmar conflitos de responsável.',
  RATE_LIMITED:
    'Muitas tentativas de confirmação. Aguarde cinco minutos antes de tentar novamente.',
  OPERATION_NOT_FOUND: 'Esta operação não está disponível nesta organização.',
  AGENDA_FORBIDDEN: 'Seu acesso não permite consultar a agenda desta organização.',
};

export function calendarDate(date: Date, utc: boolean): string {
  const year = utc ? date.getUTCFullYear() : date.getFullYear();
  const month = (utc ? date.getUTCMonth() : date.getMonth()) + 1;
  const day = utc ? date.getUTCDate() : date.getDate();
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
export function moveCalendarDate(day: string, count: number): string {
  const date = new Date(`${day}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + count);
  return calendarDate(date, true);
}
export function moveCalendarMonth(day: string, count: number): string {
  const date = new Date(`${day.slice(0, 7)}-01T12:00:00Z`);
  date.setUTCMonth(date.getUTCMonth() + count);
  return calendarDate(date, true);
}
export type CalendarView = 'day' | 'week' | 'month';
export function calendarPeriod(day: string, view: CalendarView, utc: boolean) {
  const weekday = new Date(`${day}T12:00:00Z`).getUTCDay();
  let first = day;
  let dayCount = 1;
  if (view === 'week') {
    first = moveCalendarDate(day, -((weekday + 6) % 7));
    dayCount = 7;
  } else if (view === 'month') {
    const monthFirst = `${day.slice(0, 7)}-01`;
    const monthFirstWeekday = new Date(`${monthFirst}T12:00:00Z`).getUTCDay();
    first = moveCalendarDate(monthFirst, -((monthFirstWeekday + 6) % 7));
    const nextMonth = moveCalendarMonth(monthFirst, 1);
    const last = moveCalendarDate(nextMonth, -1);
    const lastWeekday = new Date(`${last}T12:00:00Z`).getUTCDay();
    const calendarLast = moveCalendarDate(last, (7 - lastWeekday) % 7);
    dayCount =
      Math.round(
        (Date.parse(`${calendarLast}T12:00:00Z`) - Date.parse(`${first}T12:00:00Z`)) / 86_400_000,
      ) + 1;
  }
  const days = Array.from({ length: dayCount }, (_, i) => moveCalendarDate(first, i));
  const instant = (value: string) => new Date(`${value}T00:00:00${utc ? 'Z' : ''}`).toISOString();
  return { days, from: instant(first), to: instant(moveCalendarDate(first, days.length)) };
}
export function displayInstant(value: string, timeZone: string, date = false) {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone,
    ...(date ? ({ day: '2-digit', month: '2-digit' } as const) : {}),
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(value));
}
export function toTimeInput(value: string, utc: boolean): string {
  const date = new Date(value);
  const hour = utc ? date.getUTCHours() : date.getHours();
  const minute = utc ? date.getUTCMinutes() : date.getMinutes();
  const second = utc ? date.getUTCSeconds() : date.getSeconds();
  return `${calendarDate(date, utc)}T${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}:${String(second).padStart(2, '0')}`;
}
export function fromTimeInput(value: string, utc: boolean): string {
  const date = new Date(`${value}${utc ? 'Z' : ''}`);
  if (
    !value ||
    !Number.isFinite(date.getTime()) ||
    toTimeInput(date.toISOString(), utc).slice(0, value.length) !== value
  )
    throw new Error('Horário inválido ou inexistente neste fuso. Revise a data e a hora.');
  return date.toISOString();
}
export function entriesForDay(entries: readonly AgendaEntry[], day: string, utc: boolean) {
  const { from, to } = calendarPeriod(day, 'day', utc);
  return entries.filter(
    (e) =>
      Date.parse(e.revisedDeparture ?? e.plannedDeparture) < Date.parse(to) &&
      Date.parse(e.revisedArrival ?? e.plannedArrival) > Date.parse(from),
  );
}

export interface CalendarLayoutEntry {
  readonly entry: AgendaEntry;
  readonly startMinute: number;
  readonly endMinute: number;
  readonly lane: number;
  readonly laneCount: number;
}

function minuteOfDay(value: string, utc: boolean): number {
  const date = new Date(value);
  return (
    (utc ? date.getUTCHours() : date.getHours()) * 60 +
    (utc ? date.getUTCMinutes() : date.getMinutes())
  );
}

export function calendarEventLayout(
  entries: readonly AgendaEntry[],
  day: string,
  utc: boolean,
): readonly CalendarLayoutEntry[] {
  const { from, to } = calendarPeriod(day, 'day', utc);
  const dayStart = Date.parse(from);
  const dayEnd = Date.parse(to);
  const items = entriesForDay(entries, day, utc)
    .map((entry) => {
      const departure = Date.parse(entry.revisedDeparture ?? entry.plannedDeparture);
      const arrival = Date.parse(entry.revisedArrival ?? entry.plannedArrival);
      return {
        entry,
        startMinute:
          departure <= dayStart ? 0 : minuteOfDay(new Date(departure).toISOString(), utc),
        endMinute: arrival >= dayEnd ? 24 * 60 : minuteOfDay(new Date(arrival).toISOString(), utc),
      };
    })
    .filter((item) => item.endMinute > item.startMinute)
    .toSorted(
      (left, right) => left.startMinute - right.startMinute || left.endMinute - right.endMinute,
    );

  const result: CalendarLayoutEntry[] = [];
  let cluster: typeof items = [];
  let clusterEnd = -1;
  const flushCluster = () => {
    if (!cluster.length) return;
    const laneEnds: number[] = [];
    const assigned = cluster.map((item) => {
      let lane = laneEnds.findIndex((end) => end <= item.startMinute);
      if (lane === -1) lane = laneEnds.length;
      laneEnds[lane] = item.endMinute;
      return { ...item, lane };
    });
    result.push(...assigned.map((item) => ({ ...item, laneCount: laneEnds.length })));
  };

  for (const item of items) {
    if (cluster.length && item.startMinute >= clusterEnd) {
      flushCluster();
      cluster = [];
      clusterEnd = -1;
    }
    cluster.push(item);
    clusterEnd = Math.max(clusterEnd, item.endMinute);
  }
  flushCluster();
  return result;
}

export function calendarSlot(day: string, minute: number, utc: boolean) {
  const normalizedMinute = Math.max(0, Math.min(24 * 60 - 30, Math.round(minute / 30) * 30));
  const hour = Math.floor(normalizedMinute / 60);
  const minutePart = normalizedMinute % 60;
  const start = new Date(
    `${day}T${String(hour).padStart(2, '0')}:${String(minutePart).padStart(2, '0')}:00${utc ? 'Z' : ''}`,
  );
  const end = new Date(start.getTime() + 60 * 60_000);
  return {
    plannedDeparture: toTimeInput(start.toISOString(), false).slice(0, 16),
    plannedArrival: toTimeInput(end.toISOString(), false).slice(0, 16),
  };
}
